import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Carousel, CarouselContent, CarouselItem, CarouselApi } from "@/components/ui/carousel"
import { WelcomeSlide } from "@/components/welcome/WelcomeSlide"
import { Moon, Sun } from "lucide-react"
import { useTheme } from "next-themes"

const WELCOME_SLIDES = [
  {
    image: "/welcome/track-expenses.png",
    title: "Track Expenses",
    description: "Easily monitor and categorize your daily spending with our intuitive expense tracking system."
  },
  {
    image: "/welcome/budget-management.png",
    title: "Budget Management",
    description: "Set budgets for different categories and stay on top of your financial goals effortlessly."
  },
  {
    image: "/welcome/visual-insights.png",
    title: "Visual Insights",
    description: "Get clear insights into your spending patterns with beautiful charts and analytics."
  }
]

export default function WelcomePage() {
  const navigate = useNavigate()
  const { theme, setTheme } = useTheme()
  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!api) return

    setCount(api.scrollSnapList().length)
    setCurrent(api.selectedScrollSnap())

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap())
    })
  }, [api])

  const handleComplete = () => {
    localStorage.setItem("moneybees_welcome_shown", "true")
    navigate("/auth")
  }

  const handleSkip = () => {
    localStorage.setItem("moneybees_welcome_shown", "true")
    navigate("/auth")
  }

  const isLastSlide = current === count - 1

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50/30 via-white to-green-50/20 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900 p-4 relative z-[9999]">
      {/* Theme Toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 right-4 rounded-full"
        onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      >
        {theme === "dark" ? (
          <Sun className="h-5 w-5" />
        ) : (
          <Moon className="h-5 w-5" />
        )}
        <span className="sr-only">Toggle theme</span>
      </Button>

      <div className="w-full max-w-md relative z-[10000]">
        {/* Carousel */}
        <div className="relative">
          <Carousel setApi={setApi} className="w-full">
            <CarouselContent>
              {WELCOME_SLIDES.map((slide, index) => (
                <CarouselItem key={index}>
                  <div className="animate-in fade-in-0 slide-in-from-bottom-4 duration-300">
                    <WelcomeSlide
                      image={slide.image}
                      title={slide.title}
                      description={slide.description}
                      lazy={index > 0}
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          {/* Pagination Dots */}
          <div className="flex justify-center gap-2 mt-6 mb-8">
            {Array.from({ length: count }).map((_, index) => (
              <button
                key={index}
                onClick={() => api?.scrollTo(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === current
                    ? "w-8 bg-[var(--brand-amber)]"
                    : "w-2 bg-muted-foreground/30"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 hover:border-[var(--brand-green)]/60 transition-colors duration-300"
              onClick={handleSkip}
            >
              Skip
            </Button>
            <Button
              className="flex-1 bg-[var(--brand-amber)] hover:bg-[var(--brand-amber)]/90 hover:shadow-[0_0_20px_rgba(var(--brand-amber-light),0.3)] transition-all duration-300"
              onClick={isLastSlide ? handleComplete : () => api?.scrollNext()}
            >
              {isLastSlide ? "Get Started" : "Next"}
            </Button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          Manage your finances with ease
        </p>
      </div>
    </div>
  )
}
